import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@/stores/createStore';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  DEFAULT_MONITORING_CONTRAST,
  DEFAULT_NUM_COMPONENTS,
  INPUT_NAMES,
} from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import type { MappedInputFeatureV1 } from '@/tools/models/templateInstance.utilities';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
} from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export const DEFAULT_DURATION: ValueWithUnitsItem = { value: 0, units: 'min' };

export interface PrincipalComponentAnalysisV2StoreState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  outputsCollapsed: boolean;
  modelPropertiesExpanded: boolean;
  numComponents: number;
  anomalyThreshold: number;
  monitoringContrast: number;
  cleanCapsules: boolean;
  capsuleMinDuration: ValueWithUnitsItem;
  capsuleMergeDuration: ValueWithUnitsItem;
  inputSignals: ToolItem[];
  condition?: ToolItem | null;
  restrictToCondition?: ToolItem | null;
  tableApplicationId?: string;
  instanceId?: string;
}

export interface PrincipalComponentAnalysisV2StoreActions {
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setOutputsCollapsed: (collapsed: boolean) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setModelPropertiesExpanded: (expanded: boolean) => void;
  setNumComponents: (numComponents: number) => void;
  setAnomalyThreshold: (anomalyThreshold: number) => void;
  setMonitoringContrast: (monitoringContrast: number) => void;
  setCleanCapsules: (cleanCapsules: boolean) => void;
  setCapsuleMinDuration: (duration: ValueWithUnitsItem) => void;
  setCapsuleMergeDuration: (duration: ValueWithUnitsItem) => void;
  addInputSignal: (signal: ToolItem) => void;
  removeInputSignal: (signal: ToolItem) => void;
  setCondition: (condition: ToolItem | null | undefined) => void;
  setRestrictToCondition: (condition: ToolItem | null | undefined) => void;
  rehydrateForEdit: (payload: ModelRehydratePayload) => void;
  rehydrate: (dehydratedState: Partial<PrincipalComponentAnalysisDehydratedStateV2>) => void;
  reset: ResettableStoreState<PrincipalComponentAnalysisV2StoreState>;
}

export type PrincipalComponentAnalysisDehydratedStateV2 = PrincipalComponentAnalysisV2StoreState;

const ITEM_PARAMETER_KEYS = ['inputSignals', 'condition', 'restrictToCondition'];

const initialState: PrincipalComponentAnalysisV2StoreState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  windowStart: 0,
  windowEnd: 0,
  outputsCollapsed: true,
  modelPropertiesExpanded: false,
  numComponents: DEFAULT_NUM_COMPONENTS,
  anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
  monitoringContrast: DEFAULT_MONITORING_CONTRAST,
  cleanCapsules: false,
  capsuleMinDuration: DEFAULT_DURATION as ValueWithUnitsItem,
  capsuleMergeDuration: DEFAULT_DURATION as ValueWithUnitsItem,
  inputSignals: [] as ToolItem[],
  condition: undefined as ToolItem | null | undefined,
  restrictToCondition: undefined as ToolItem | null | undefined,
  tableApplicationId: undefined as string | undefined,
  instanceId: undefined as string | undefined,
});

const principalComponentAnalysisV2ConfigParamsSelector = createSelector(
  [(state: PrincipalComponentAnalysisV2StoreState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V_2,
      parameterDefinitionKeys: ITEM_PARAMETER_KEYS,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });
    return _.omit(config, ['modelPropertiesExpanded']) as ToolConfigParams;
  },
);

const principalComponentAnalysisV2WindowSelector = createSelector(
  [
    (state: PrincipalComponentAnalysisV2StoreState) => state.windowStart,
    (state: PrincipalComponentAnalysisV2StoreState) => state.windowEnd,
  ],
  (windowStart, windowEnd) => ({ startTime: windowStart, endTime: windowEnd }),
);

export const dehydratePrincipalComponentAnalysisV2StoreState = (
  state: PrincipalComponentAnalysisV2StoreState,
): PrincipalComponentAnalysisDehydratedStateV2 => state;

const principalComponentAnalysisV2ActionsFactory: ActionsFactory<
  PrincipalComponentAnalysisV2StoreState,
  PrincipalComponentAnalysisV2StoreActions
> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setOutputsCollapsed: (collapsed) =>
    set((draft) => {
      draft.outputsCollapsed = collapsed;
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setModelPropertiesExpanded: (expanded) =>
    set((draft) => {
      draft.modelPropertiesExpanded = expanded;
    }),

  setNumComponents: (numComponents) =>
    set((draft) => {
      draft.numComponents = numComponents;
    }),

  setAnomalyThreshold: (anomalyThreshold) =>
    set((draft) => {
      draft.anomalyThreshold = anomalyThreshold;
    }),

  setMonitoringContrast: (monitoringContrast) =>
    set((draft) => {
      draft.monitoringContrast = monitoringContrast;
    }),

  setCleanCapsules: (cleanCapsules) =>
    set((draft) => {
      draft.cleanCapsules = cleanCapsules;
    }),

  setCapsuleMinDuration: (duration) =>
    set((draft) => {
      draft.capsuleMinDuration = duration;
    }),

  setCapsuleMergeDuration: (duration) =>
    set((draft) => {
      draft.capsuleMergeDuration = duration;
    }),

  addInputSignal: (signal) =>
    set((draft) => {
      const sanitized = sanitizeToolItem(signal);
      if (sanitized && !draft.inputSignals.some((s) => s.id === sanitized.id)) {
        draft.inputSignals = draft.inputSignals.concat(sanitized);
      }
    }),

  removeInputSignal: (signal) =>
    set((draft) => {
      draft.inputSignals = draft.inputSignals.filter((s) => s.id !== signal?.id);
    }),

  setCondition: (item) =>
    set((draft) => {
      draft.condition = sanitizeToolItem(item) as PrincipalComponentAnalysisV2StoreState['condition'];
    }),

  setRestrictToCondition: (item) =>
    set((draft) => {
      draft.restrictToCondition = sanitizeToolItem(
        item,
      ) as PrincipalComponentAnalysisV2StoreState['restrictToCondition'];
    }),

  rehydrateForEdit: (payload: ModelRehydratePayload) => {
    resetState();
    const inputSignals: ToolItem[] = [];
    set((draft) => {
      draft.id = payload.id;
      draft.instanceId = payload.instanceId;
      draft.tableApplicationId = payload.instanceData?.tableApplicationId;
      (payload.modelData?.inputFeatures ?? []).forEach((feature: MappedInputFeatureV1): void => {
        const inputName: string = feature.name!;
        switch (inputName) {
          case SeeqNames.Models.InstanceNameInputFeatureName:
            // TODO: CRAB-53864: Handle duplicate names more gracefully
            draft.name = payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`;
            break;
          case INPUT_NAMES.anomalyThresholdInput:
            draft.anomalyThreshold = feature.inputNumeric ?? DEFAULT_ANOMALY_THRESHOLD;
            break;
          case INPUT_NAMES.nComponentsInput:
            draft.numComponents = feature.inputNumeric ?? DEFAULT_NUM_COMPONENTS;
            break;
          case INPUT_NAMES.monitoringSignalContrastInput:
            draft.monitoringContrast = feature.inputNumeric ?? DEFAULT_MONITORING_CONTRAST;
            break;
          case INPUT_NAMES.startTimeInput: {
            const startTimeInput = JSON.parse(feature.inputText!);
            draft.windowStart = moment(startTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.endTimeInput: {
            const endTimeInput = JSON.parse(feature.inputText!);
            draft.windowEnd = moment(endTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.mergeCapsuleDurationInput: {
            draft.cleanCapsules = !_.isNil(feature.inputNumeric);
            draft.capsuleMergeDuration = {
              value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
              units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
            };
            break;
          }
          case INPUT_NAMES.minCapsuleDurationInput: {
            draft.cleanCapsules = !_.isNil(feature.inputNumeric);
            draft.capsuleMinDuration = {
              value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
              units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
            };
            break;
          }
          case INPUT_NAMES.trainWithinConditionInput:
            if (feature.inputItem) {
              draft.condition = { id: feature.inputItem };
            }
            break;
          case INPUT_NAMES.restrictOutputToConditionInput: {
            if (feature.inputItem) {
              draft.restrictToCondition = { id: feature.inputItem };
            }
            break;
          }
          default:
            if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
              inputSignals.push({ id: feature.inputItem });
            }
            break;
        }
      });
      draft.inputSignals = inputSignals;
    });
  },

  rehydrate: (dehydratedState) =>
    set((draft) => {
      Object.assign(draft, dehydratedState);
    }),

  reset: (overrides) => resetState(overrides),
});

const [
  PrincipalComponentAnalysisV2StoreProvider,
  usePrincipalComponentAnalysisV2StoreInstance,
  usePrincipalComponentAnalysisV2StoreSelector,
] = createStore('PrincipalComponentAnalysisV2', initialState, principalComponentAnalysisV2ActionsFactory);

export { PrincipalComponentAnalysisV2StoreProvider };

export const usePrincipalComponentAnalysisV2ToolId = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.id);
export const usePrincipalComponentAnalysisV2ToolName = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.name);
export const usePrincipalComponentAnalysisV2ToolOriginalParameters = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.originalParameters);
export const usePrincipalComponentAnalysisV2ToolAdvancedParametersCollapsed = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.advancedParametersCollapsed);
export const usePrincipalComponentAnalysisV2ToolOutputsCollapsed = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.outputsCollapsed);
export const usePrincipalComponentAnalysisV2ToolWindow = () =>
  usePrincipalComponentAnalysisV2StoreSelector(principalComponentAnalysisV2WindowSelector);
export const usePrincipalComponentAnalysisV2ToolModelPropertiesExpanded = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.modelPropertiesExpanded);
export const usePrincipalComponentAnalysisV2ToolNumComponents = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.numComponents);
export const usePrincipalComponentAnalysisV2ToolAnomalyThreshold = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.anomalyThreshold);
export const usePrincipalComponentAnalysisV2ToolMonitoringContrast = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.monitoringContrast);
export const usePrincipalComponentAnalysisV2ToolCleanCapsules = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.cleanCapsules);
export const usePrincipalComponentAnalysisV2ToolCapsuleMinDuration = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.capsuleMinDuration);
export const usePrincipalComponentAnalysisV2ToolCapsuleMergeDuration = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.capsuleMergeDuration);
export const usePrincipalComponentAnalysisV2ToolInputSignals = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.inputSignals);
export const usePrincipalComponentAnalysisV2ToolCondition = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.condition);
export const usePrincipalComponentAnalysisV2ToolRestrictToCondition = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.restrictToCondition);
export const usePrincipalComponentAnalysisV2ToolInstanceId = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.instanceId);
export const usePrincipalComponentAnalysisV2ToolTableApplicationId = () =>
  usePrincipalComponentAnalysisV2StoreSelector((state) => state.tableApplicationId);
export const usePrincipalComponentAnalysisV2ToolConfigParams = () =>
  usePrincipalComponentAnalysisV2StoreSelector(principalComponentAnalysisV2ConfigParamsSelector);

export const usePrincipalComponentAnalysisV2ToolActions = () => usePrincipalComponentAnalysisV2StoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestPrincipalComponentAnalysisV2Store(
  overrides?: Partial<PrincipalComponentAnalysisV2StoreState>,
) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestPrincipalComponentAnalysisV2Store() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, principalComponentAnalysisV2ActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (utility files)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreatePrincipalComponentAnalysisV2Store() {
  return createStoreInstance<PrincipalComponentAnalysisV2StoreState, PrincipalComponentAnalysisV2StoreActions>(
    initialState,
    principalComponentAnalysisV2ActionsFactory,
  );
}

export const usePersistPrincipalComponentAnalysisV2Store = () => {
  const register = useRegisterPersistedStore();
  const store = usePrincipalComponentAnalysisV2StoreInstance();

  useEffect(() => {
    return register({
      storeName: 'principalComponentAnalysisV2Store',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydratePrincipalComponentAnalysisV2StoreState,
    });
  }, [register, store]);
};
